import { FC } from "react";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { NextCorporateAction, Stock } from "@/models/stock";
import { formatDate } from "@/lib/utils";

interface Props {
    stock: Stock;
    // Tighter padding / smaller text variant for the mobile slot under
    // the company subtitle (same split as StockReportBadge). Desktop
    // renders a session-chip-family MUI Chip next to the market-hours
    // indicator.
    compact?: boolean;
}

/**
 * Presentation split of the backend `next_corporate_action` payload:
 * a leading type label, the formatted ex-date, and the short detail
 * string ("0.26 USD", "1:20").
 *
 * Split details arrive as "<ratio> <Forward|Reverse> split" — fold the
 * direction into the type label so the chip doesn't read
 * "Split · 1:20 Reverse split". Unrecognised shapes fall back to the
 * detail string untouched.
 */
const presentAction = (action: NextCorporateAction): { label: string; detail: string } => {
    if (action.type === "split") {
        const match = /^(\S+)\s+(Forward|Reverse)\s+split$/i.exec(action.detail ?? "");
        if (match) return { label: `${match[2]} split`, detail: match[1] };
        return { label: "Split", detail: action.detail ?? "" };
    }
    return { label: "Ex-dividend", detail: action.detail ?? "" };
};

/**
 * "Next corporate action" chip rendered in the stock header — the
 * nearest upcoming ex-dividend or split from the stock base-data
 * payload (`stock.next_corporate_action`).
 *
 * Renders nothing when the field is null/absent (no upcoming action
 * inside the backend's lookahead window) or when the cached payload's
 * ex-date has already passed — a "next action" chip pointing at
 * yesterday is worse than no chip. Never lays out placeholder space.
 *
 * Deliberately the neutral session-chip family (outlined, theme
 * divider/text tokens) rather than the primary-coloured CTA badges
 * (Stock Report / Trading Idea): this is data about the stock, not a
 * call-to-action, so it should sit quietly next to the market-hours
 * chip. Click → the dividend tab for dividends, the corporate-actions
 * tab for splits.
 */
const NextCorporateActionChip: FC<Props> = ({ stock, compact = false }) => {
    const router = useRouter();

    const action = stock?.next_corporate_action;
    if (!action || !action.type || !action.date || !action.detail) return null;
    if (!stock?.ticker || !stock?.exchangesymbol) return null;

    const exDate = dayjs(action.date);
    // Base data is cached (~1h) and a page can stay open across midnight —
    // hide once the ex-date is behind us rather than advertise a stale one.
    if (!exDate.isValid() || exDate.isBefore(dayjs().startOf("day"))) return null;

    const { label, detail } = presentAction(action);
    const dateLabel = formatDate(exDate);
    const target = `/s/${stock.exchangesymbol}/${stock.ticker}/${action.type === "dividend" ? "dividend" : "corporate-actions/all"}`;
    const Icon = action.type === "split" ? CallSplitIcon : PaidOutlinedIcon;
    const ariaLabel = `Next corporate action: ${label} on ${exDate.format("DD MMM YYYY")}, ${detail}`;
    const tooltip = `Next corporate action: ${label.toLowerCase()} ${exDate.format("DD MMM YYYY")}. Click for details.`;

    // Mobile: badge-family pill on its own line under the subtitle
    // (see RootLayout), neutral chrome instead of the CTA badges'
    // primary colour.
    if (compact) {
        return (
            <Box
                onClick={() => router.push(target)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && router.push(target)}
                aria-label={ariaLabel}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.25,
                    mt: 0.75,
                    mr: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    color: "text.secondary",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: "action.hover" },
                }}
            >
                <Icon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.3 }}>
                    {label} {dateLabel} · {detail}
                </Typography>
            </Box>
        );
    }

    // Desktop: sibling of the StockHeaderSession chip (outlined MUI
    // Chip, captionSessionChip typography, icon-left, opacity 0.8) so
    // the pair reads as one informational chip family.
    return (
        <Tooltip title={tooltip} arrow>
            <Chip
                size="small"
                variant="outlined"
                clickable
                icon={<Icon sx={{ fontSize: 14 }} />}
                label={
                    <Typography variant="captionSessionChip">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {label}
                        </Box>
                        {` ${dateLabel} · ${detail}`}
                    </Typography>
                }
                onClick={() => router.push(target)}
                onKeyDown={(e) => e.key === "Enter" && router.push(target)}
                aria-label={ariaLabel}
                sx={{ mr: 2, px: 0.5, cursor: "pointer", opacity: 0.8, "& .MuiChip-icon": { color: "text.secondary", ml: 0.5 } }}
            />
        </Tooltip>
    );
};

export default NextCorporateActionChip;
